/**
 * @fileoverview Componente Molecule para acciones rápidas sobre notas de orden en el Inbox.
 * @module OrderNoteActionsMC
 * @category Component - Molecule
 * @since 3.1.0
 * @version 3.1.0
 *
 * @description
 * Componente optimizado que renderiza las acciones rápidas de una nota de orden:
 * - Enviar PDF
 * - Enviar link de pago
 * - Enviar por plataforma (WhatsApp, Messenger, etc.)
 *
 * Utiliza memoización avanzada para labels, iconos y handlers, siguiendo Atomic Design y buenas prácticas de performance.
 *
 * @component OrderNoteActionsMC
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.order - Objeto de la orden
 * @param {string} props.platform - Plataforma destino ('whatsapp', 'messenger', etc.)
 * @param {function} props.generateOrderLink - Handler para generar link de pago
 * @param {function} props.sendOrder - Handler para enviar por plataforma
 * @param {function} props.generateOrderPdf - Handler para generar PDF
 * @param {boolean} props.hideSendPdf - Si se oculta la acción de PDF
 * @param {string} props.platformName - Nombre de la plataforma para mostrar en el label
 *
 * @returns {JSX.Element} Elemento JSX renderizado
 *
 * @example
 * <OrderNoteActionsMC
 *   order={order}
 *   platform="whatsapp"
 *   generateOrderLink={fn}
 *   sendOrder={fn}
 *   generateOrderPdf={fn}
 *   hideSendPdf={false}
 *   platformName="WhatsApp"
 * />
 *
 * @see OrderNoteActionsItemMC
 */
import React, { memo, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PLATFORM_CONFIG from '../../../utils/platformConfig';
import OrderNoteActionsItemMC from './OrderNoteActionsItemMC';

const OrderNoteActionsMC = memo(({
  order,
  platform,
  generateOrderLink,
  sendOrder,
  generateOrderPdf,
  hideSendPdf,
  platformName
}) => {
  const { t } = useTranslation('InboxMC');
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer) || {};
  const showPaymentLink = retailer_info?.connected_to_wava;

  // Memoizar labels para evitar recomputación
  const pdfLabel = useMemo(() => t('orderNote.send_pdf').split('\n'), [t]);
  const linkLabel = useMemo(() => t('orderNote.send_link').split('\n'), [t]);
  const sendByLabel = useMemo(() => [t('orderNote.send_by'), platformName], [t, platformName]);

  // Memoizar handlers para evitar recreación
  const handlePdf = useCallback(() => generateOrderPdf(order), [generateOrderPdf, order]);
  const handleLink = useCallback(() => generateOrderLink(order), [generateOrderLink, order]);
  const handleSend = useCallback(() => sendOrder(order), [sendOrder, order]);

  // Memoizar icon y size
  const platformIcon = useMemo(() => PLATFORM_CONFIG[platform]?.icon, [platform]);
  return (
    <div className="tw-mt-2">
      <div className="tw-flex tw-justify-between">
        {hideSendPdf && <div>&nbsp;</div>}
        {!hideSendPdf && (
          <OrderNoteActionsItemMC
            onClick={handlePdf}
            iconName="pdf-file-outline"
            labelLines={pdfLabel}
          />
        )}

        <OrderNoteActionsItemMC
          onClick={handleLink}
          iconName="link-outline"
          labelLines={linkLabel}
        />

        {showPaymentLink && (
          <OrderNoteActionsItemMC
            onClick={handleLink}
            iconName="link-outline"
            labelLines={linkLabel}
          />
        )}

        <OrderNoteActionsItemMC
          onClick={handleSend}
          iconName={platformIcon}
          labelLines={sendByLabel}
          size="2xl"
        />

        {hideSendPdf && <div>&nbsp;</div>}
      </div>
    </div>
  );
});

OrderNoteActionsMC.displayName = 'OrderNoteActionsMC';
export default OrderNoteActionsMC;
